import { useEffect, useState } from "react";

import { getQuote } from "../../lib/api/contractor";
import { dateOnly, money } from "../../lib/format";
import { parseQuoteStatus } from "../../lib/types/enums";
import type { Message, Quote } from "../../lib/types/models";
import { ClockIcon, MoneyIcon } from "@ui/react/icons";
import { Button, Skeleton, StatusPill } from "@ui/react";
import { metaString } from "./metadata";
import { errorMessage } from "../../lib/errorMessage";

interface QuoteState {
  loading: boolean;
  error: unknown;
  quote: Quote | null;
}

/**
 * A quote message rendered as a card.
 *
 * ISSUE #40, do not regress: the amount comes from the QUOTE document fetched
 * by id, never from the message metadata. That blob is empty for every quote
 * the real flow produces, so reading the total from it rendered "R0" on live
 * quotes. Top-level /quotes has no client read rule in firestore.rules either,
 * so REST is the only route to the authoritative figure — hence a skeleton
 * while it is in flight instead of a placeholder total.
 */
export function QuoteCard({ message }: { message: Message }) {
  const quoteId = message.quoteId ?? metaString(message.metadata, "quoteId");

  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<QuoteState>({
    loading: Boolean(quoteId),
    error: null,
    quote: null,
  });

  useEffect(() => {
    if (!quoteId) {
      setState({ loading: false, error: null, quote: null });
      return;
    }

    let ignore = false;
    setState({ loading: true, error: null, quote: null });

    getQuote(quoteId)
      .then((quote) => {
        if (!ignore) setState({ loading: false, error: null, quote });
      })
      .catch((error: unknown) => {
        if (!ignore) setState({ loading: false, error, quote: null });
      });

    return () => {
      ignore = true;
    };
  }, [quoteId, attempt]);

  if (state.loading) {
    return (
      <div className="thread-card" role="status" aria-label="Loading quote">
        <span className="cluster">
          <MoneyIcon />
          <span className="row__title">Quote</span>
        </span>
        <Skeleton height="2rem" />
        <Skeleton height="3rem" />
      </div>
    );
  }

  const quote = state.quote;

  if (!quote) {
    // Either the message carries no quote id, or the fetch failed — a quote is
    // readable only by its author, so a 403 is a real outcome on a thread whose
    // quote was written by someone else. Show what the message itself said and
    // admit the gap; inventing a total would be the #40 bug all over again.
    return (
      <div className="thread-card">
        <span className="cluster">
          <MoneyIcon />
          <span className="row__title">Quote</span>
        </span>
        <p className="muted">{message.content}</p>
        <p className="dim">
          {state.error
            ? `Amount unavailable — ${errorMessage(state.error)}`
            : "This message has no quote attached."}
        </p>
        {state.error ? (
          <span className="cluster">
            <Button variant="tertiary" onClick={() => setAttempt((value) => value + 1)}>
              Try again
            </Button>
          </span>
        ) : null}
      </div>
    );
  }

  const status = parseQuoteStatus(quote.status);

  return (
    <div className="thread-card">
      <span className="cluster cluster--between">
        <span className="cluster">
          <MoneyIcon />
          <span className="row__title">Quote</span>
        </span>
        <StatusPill kind="quote" status={status} />
      </span>

      <span className="cluster cluster--between">
        <span className="muted">Total</span>
        <span className="amount">{money(quote.amount, quote.currency)}</span>
      </span>

      {quote.description ? <p className="muted">{quote.description}</p> : null}

      <span className="cluster dim">
        <ClockIcon />
        <span>{quote.estimatedDays === 1 ? "1 day" : `${quote.estimatedDays} days`} estimated</span>
      </span>

      {quote.validUntil ? (
        <span className="dim">Valid until {dateOnly(quote.validUntil)}</span>
      ) : null}
    </div>
  );
}
